import { useState, useEffect } from "react";
import { 
  AlertTriangle, Phone, Clock, Calendar, X, RefreshCw, 
  ChevronDown, ChevronUp, ExternalLink, Bell, CheckCircle2
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function AlertesConfirmationRDV({ collaborateur = "", onRefresh, defaultExpanded = true }) {
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState({ total: 0, critiques: 0, hautes: 0, moderees: 0 });
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAlertes();
    // Refresh toutes les 5 minutes
    const interval = setInterval(loadAlertes, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [collaborateur]);

  const loadAlertes = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = collaborateur 
        ? `${API}/alertes/confirmation-rdv?collaborateur=${encodeURIComponent(collaborateur)}&include_all=true`
        : `${API}/alertes/confirmation-rdv?include_all=true`;
      const resp = await authFetch(url);
      if (!resp.ok) throw new Error("Erreur chargement alertes");
      const data = await resp.json();
      setAlertes(data.alertes || []);
      setStats(data.stats || {});
      setConfig(data.config || {});
    } catch (e) {
      console.error("Erreur chargement alertes RDV:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCallClient = (telephone) => {
    if (telephone) {
      window.open(`tel:${telephone.replace(/\s/g, '')}`, '_blank');
    }
  };

  const getUrgenceStyle = (urgence) => {
    switch (urgence) {
      case "MANQUÉ":
        return "bg-red-900/50 border-red-500/50 text-red-200";
      case "CRITIQUE":
        return "bg-red-800/50 border-red-400/50 text-red-100";
      case "HAUTE":
        return "bg-orange-800/50 border-orange-400/50 text-orange-100";
      case "MODÉRÉE":
        return "bg-blue-800/50 border-blue-400/50 text-blue-100";
      default:
        return "bg-slate-800/50 border-slate-600/50 text-slate-200";
    }
  };

  const getUrgenceBadge = (urgence) => {
    switch (urgence) {
      case "MANQUÉ":
        return "bg-red-500 text-white";
      case "CRITIQUE":
        return "bg-red-600 text-white";
      case "HAUTE":
        return "bg-orange-500 text-white";
      case "MODÉRÉE":
        return "bg-blue-500 text-white";
      default:
        return "bg-slate-500 text-white";
    }
  };

  // Aucune alerte critique ou haute = tout va bien
  if (!loading && stats.critiques === 0 && stats.hautes === 0) {
    return (
      <div data-testid="alertes-rdv-ok" className="bg-green-900/20 border border-green-500/30 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-green-300">Tous les RDV sont confirmés !</h3>
            <p className="text-sm text-green-400/70">
              {config.jour_actuel} {config.heure_actuelle} - Heure limite: {config.heure_limite}
            </p>
          </div>
          <button
            onClick={loadAlertes}
            className="p-2 hover:bg-green-500/20 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 text-green-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="alertes-rdv-container" className="bg-gradient-to-r from-red-900/20 to-orange-900/20 border border-red-500/30 rounded-xl mb-4 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center animate-pulse">
              <Bell className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-bold text-white flex items-center gap-2">
                Alertes Confirmation RDV
                {stats.critiques > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                    {stats.critiques} critique{stats.critiques > 1 ? 's' : ''}
                  </span>
                )}
                {stats.hautes > 0 && (
                  <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                    {stats.hautes} urgent{stats.hautes > 1 ? 's' : ''}
                  </span>
                )}
              </h3>
              <p className="text-sm text-slate-400">
                {config.jour_actuel} {config.heure_actuelle} - Limite: {config.heure_limite}
                {!config.avant_heure_limite && " (dépassée)"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); loadAlertes(); }}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {/* Liste des alertes */}
      {expanded && (
        <div className="border-t border-slate-700/50 p-4 space-y-3 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-400">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          ) : alertes.length === 0 ? (
            <div className="text-center py-4 text-slate-400">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <p>Aucune alerte en cours</p>
            </div>
          ) : (
            <>
              {alertes.map((alerte, idx) => (
                <div
                  key={alerte.id || idx}
                  data-testid={`alerte-rdv-${idx}`}
                  className={`rounded-lg border p-3 ${getUrgenceStyle(alerte.alerte.urgence)}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${getUrgenceBadge(alerte.alerte.urgence)}`}>
                          {alerte.alerte.urgence}
                        </span>
                        <span className="text-xs text-slate-400">
                          {alerte.dateFr} ({alerte.jourSemaine})
                        </span>
                      </div>
                      <h4 className="font-semibold text-white truncate">
                        {alerte.client}
                      </h4>
                      <p className="text-sm opacity-80 mt-1">
                        {alerte.alerte.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        {alerte.departement && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Dpt {alerte.departement}
                          </span>
                        )}
                        {alerte.telepro && (
                          <span>Télépro: {alerte.telepro}</span>
                        )}
                        {alerte.technicien && (
                          <span>Tech: {alerte.technicien}</span>
                        )}
                      </div>
                    </div>
                    {alerte.telephone && (
                      <button
                        onClick={() => handleCallClient(alerte.telephone)}
                        className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors"
                        title={`Appeler ${alerte.telephone}`}
                      >
                        <Phone className="w-4 h-4" />
                        <span className="hidden sm:inline">Appeler</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs mt-2 text-slate-300 italic">
                    ➜ {alerte.alerte.action}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
